// Cache Management Service
// Handles cache lifecycle, cleanup, and performance monitoring

import { cacheService } from './cacheService';

class CacheManager {
  constructor() {
    this.cleanupInterval = null;
    this.isInitialized = false;
  }

  /**
   * Initialize cache manager with automatic cleanup
   */
  init() {
    if (this.isInitialized) return;

    // Clean up expired entries on startup
    this.cleanupExpiredEntries();

    // Set up automatic cleanup every 30 minutes
    this.cleanupInterval = setInterval(() => {
      this.cleanupExpiredEntries();
    }, 30 * 60 * 1000);

    // Clean up when window is about to unload
    window.addEventListener('beforeunload', () => {
      this.cleanup();
    });

    // Clean up when page becomes hidden (mobile/tab switching)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.cleanupExpiredEntries();
      }
    });

    this.isInitialized = true;
    console.log('Cache manager initialized');
  }

  /**
   * Clean up expired cache entries
   */
  cleanupExpiredEntries() {
    try {
      const beforeStats = cacheService.getCacheStats();
      cacheService.clearExpired();
      const afterStats = cacheService.getCacheStats();
      
      const cleaned = beforeStats.totalEntries - afterStats.totalEntries;
      if (cleaned > 0) {
        console.log(`Cache cleanup: removed ${cleaned} expired entries`);
      }
    } catch (error) {
      console.warn('Cache cleanup failed:', error);
    }
  }

  /**
   * Invalidate cache for specific data types
   */
  invalidateUserData(userId) {
    if (userId) {
      cacheService.removeUserProfile(userId);
      console.log(`Invalidated cache for user: ${userId}`);
    }
  }

  invalidateLeagueData(leagueId) {
    if (leagueId) {
      cacheService.remove(cacheService.getCacheKey('leagueInfo', leagueId));
      // Don't invalidate historical standings as they never change
      console.log(`Invalidated league cache for: ${leagueId}`);
    }
  }

  invalidateSeasonData(seasonYear) {
    if (seasonYear) {
      cacheService.remove(cacheService.getCacheKey('seasonConfig', seasonYear));
      console.log(`Invalidated season cache for: ${seasonYear}`);
    }
  }

  /**
   * Get cache performance metrics
   */
  getPerformanceMetrics() {
    const stats = cacheService.getCacheStats();
    const sizeInMB = (stats.totalSize / 1024 / 1024).toFixed(2);
    
    return {
      ...stats,
      sizeInMB,
      avgEntrySize: stats.totalEntries > 0 ? Math.round(stats.totalSize / stats.totalEntries) : 0,
      storageUsage: this.getStorageUsage()
    };
  }

  /**
   * Get localStorage usage percentage
   */
  getStorageUsage() {
    try {
      // Test localStorage quota (this is approximate)
      const testKey = '__storage_test__';
      const testData = 'x'.repeat(1024); // 1KB test
      let used = 0;
      
      // Get current usage
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          used += localStorage[key].length + key.length;
        }
      }

      localStorage.setItem(testKey, testData);
      localStorage.removeItem(testKey);

      // Convert to MB
      const usedMB = (used / 1024 / 1024).toFixed(2);
      
      return {
        usedBytes: used,
        usedMB,
        // Most browsers have 5-10MB localStorage limit
        estimatedQuotaMB: 10,
        usagePercent: Math.round((used / (10 * 1024 * 1024)) * 100)
      };
    } catch (error) {
      return {
        usedBytes: 0,
        usedMB: '0.00',
        estimatedQuotaMB: 10,
        usagePercent: 0,
        error: 'Unable to calculate storage usage'
      };
    }
  }

  /**
   * Emergency cache clear if storage is nearly full
   */
  emergencyCacheClear() {
    const usage = this.getStorageUsage();
    
    if (usage.usagePercent > 90) {
      console.warn('Storage nearly full, clearing non-essential cache');
      
      // Clear least important cache first
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith('prempicks_')
      );

      // Priority order: seasonConfig > leagueInfo > userProfile > historicalStandings (keep historical data)
      const priorityOrder = ['seasonConfig', 'leagueInfo', 'userProfile'];
      
      for (const type of priorityOrder) {
        const typeKeys = keys.filter(key => key.includes(`_${type}_`));
        typeKeys.forEach(key => localStorage.removeItem(key));
        
        const newUsage = this.getStorageUsage();
        if (newUsage.usagePercent < 70) {
          console.log(`Cleared ${type} cache, usage now at ${newUsage.usagePercent}%`);
          break;
        }
      }
    }
  }

  /**
   * Preload common data in background
   */
  async preloadCriticalData(user, currentLeague) {
    try {
      if (user && !cacheService.getUserProfile(user.uid)) {
        // This will trigger background loading in LeagueProvider
        console.log('Preloading user data in background');
      }

      if (currentLeague && !cacheService.getLeagueInfo(currentLeague.id)) {
        console.log('Preloading league data in background');
      }
    } catch (error) {
      console.warn('Preload failed:', error);
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.isInitialized = false;
  }

  /**
   * Reset all cache (for debugging or major app updates)
   */
  resetAllCache() {
    const confirmed = window.confirm('This will clear all cached data. Are you sure?');
    if (confirmed) {
      cacheService.clearAll();
      console.log('All cache cleared');
      window.location.reload();
    }
  }
}

// Export singleton instance
export const cacheManager = new CacheManager();
export default CacheManager;